import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowRight02Icon } from "@hugeicons/core-free-icons";
import { DateRange } from "react-day-picker";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Button } from "../ui/button";
import formatPerisanDate from "@/utils/formatPerisanDate";
import { DestinationSearchModal } from "../home/DestinationSearchModal";
import DateRangePickerModal from "../shared/DateRangePickerModal";
import { MobileSearchSummaryButton } from "./MobileSearchSummaryButton";
import { useCities } from "@/lib/react-query/queries/references/use-cities";
import type { City } from "@/types/city";

interface MobileSearchStepperProps {
  onSubmit: () => void;
  watch: <T extends keyof SearchFormData>(name: T) => SearchFormData[T];
  setValue: <T extends keyof SearchFormData>(
    name: T,
    value: SearchFormData[T]
  ) => void;
  errors: Partial<Record<keyof SearchFormData, string | undefined>>;
}

interface SearchFormData {
  destination: string;
  dateRange: {
    from: Date;
    to?: Date;
  };
  peopleCount: number;
}

const MobileSearchStepper: React.FC<MobileSearchStepperProps> = ({
  onSubmit,
  watch,
  setValue,
  errors,
}) => {
  const isMobile = useMediaQuery("(max-width: 1024px)");
  const [destinationOpen, setDestinationOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [destinationSearch, setDestinationSearch] = useState("");
  const router = useRouter();
  const { data: citiesResponse } = useCities();
  const cities = citiesResponse?.data || [];

  // Back arrow handler
  const handleBack = () => {
    router.back();
  };

  const handleDestinationOpenChange = (open: boolean) => {
    setDestinationOpen(open);
    if (!open) setDestinationSearch("");
  };

  // Handle destination select
  const handleDestinationSelect = (value: string) => {
    setValue("destination", value);
    setDestinationSearch("");
    setDestinationOpen(false);
    setDateOpen(true); // Open date picker after closing destination
  };

  // Handle date select
  const handleDateRangeSelect = (range: DateRange | undefined) => {
    if (range?.from) setValue("dateRange", { from: range.from, to: range.to });
    if (range?.from && range?.to) {
      onSubmit();
    }
  };

  if (!isMobile) return null;

  const destination = watch("destination");
  const dateRange = watch("dateRange");

  // Get display name for destination (convert slug to city name)
  const getDisplayName = (slug: string | undefined): string => {
    if (!slug) return "";

    // Find city by slug
    const city = cities.find((c: City) => c.slug === slug);
    if (city) {
      return city.name;
    }

    // If not found in cities, it might be a province name or recent search
    return slug;
  };

  const destinationDisplayName = getDisplayName(destination);

  let dateValue = "";
  if (dateRange?.from && dateRange?.to) {
    dateValue = `${formatPerisanDate(dateRange.from)} - ${formatPerisanDate(
      dateRange.to
    )}`;
  } else if (dateRange?.from) {
    dateValue = formatPerisanDate(dateRange.from);
  }

  return (
    <div className="w-full flex items-center gap-2 px-5 md:px-0">
      <Button
        variant="ghost"
        size="icon"
        onClick={handleBack}
        className="p-0 w-auto"
      >
        <HugeiconsIcon icon={ArrowRight02Icon} className="size-6" />
      </Button>
      <MobileSearchSummaryButton
        destination={destinationDisplayName}
        dateValue={dateValue}
        onClick={() => setDestinationOpen(true)}
        error={errors.destination as string}
      />
      <div className="hidden">
        <DestinationSearchModal
          trigger={<div />}
          open={destinationOpen}
          onOpenChange={handleDestinationOpenChange}
          search={destinationSearch}
          onSearchChange={setDestinationSearch}
          isMobile
          onSelect={handleDestinationSelect}
        />
        <DateRangePickerModal
          open={dateOpen}
          setOpen={setDateOpen}
          dateRange={dateRange}
          onSelect={(range) => handleDateRangeSelect(range)}
        />
      </div>
    </div>
  );
};

export default MobileSearchStepper;
